import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'client';
  avatarUrl: string | null;
  organizationId: string | null;
  hasPassword?: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  rememberMe: boolean;

  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginWithMagicLink: (token: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setRememberMe: (value: boolean) => void;
  updateProfile: (data: { name?: string; avatarUrl?: string }) => Promise<void>;
  updatePassword: (currentPassword: string | null, newPassword: string) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
}

// Session cleanup handler for when rememberMe is false
let cleanupHandler: (() => void) | null = null;

function setupSessionCleanup() {
  if (cleanupHandler) return; // Already set up

  cleanupHandler = () => {
    // Clear auth storage on browser close when rememberMe is false
    localStorage.removeItem('yoda-client-auth');
  };

  window.addEventListener('beforeunload', cleanupHandler);
}

function removeSessionCleanup() {
  if (cleanupHandler) {
    window.removeEventListener('beforeunload', cleanupHandler);
    cleanupHandler = null;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      rememberMe: true,

      setRememberMe: (value: boolean) => {
        set({ rememberMe: value });
        if (value) {
          removeSessionCleanup();
        } else {
          setupSessionCleanup();
        }
      },

      login: async (email: string, password: string, rememberMe = true) => {
        const response = await api.post<{
          user: User;
          accessToken: string;
          refreshToken: string;
        }>('/auth/login', { email, password });

        // Setup session cleanup if not remembering
        if (!rememberMe) {
          setupSessionCleanup();
        } else {
          removeSessionCleanup();
        }

        set({
          user: response.data.user,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          isAuthenticated: true,
          isLoading: false,
          rememberMe,
        });
      },

      loginWithMagicLink: async (token: string, rememberMe?: boolean) => {
        // Check for pending rememberMe preference from login page
        const pendingRememberMe = localStorage.getItem('yoda-remember-me-pending');
        const shouldRemember = rememberMe ?? (pendingRememberMe === 'true');
        localStorage.removeItem('yoda-remember-me-pending');

        const response = await api.get<{
          user: User;
          accessToken: string;
          refreshToken: string;
        }>(`/auth/verify/${token}`);

        // Setup session cleanup if not remembering
        if (!shouldRemember) {
          setupSessionCleanup();
        } else {
          removeSessionCleanup();
        }

        set({
          user: response.data.user,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          isAuthenticated: true,
          isLoading: false,
          rememberMe: shouldRemember,
        });
      },

      logout: async () => {
        const { refreshToken } = get();
        try {
          await api.post('/auth/logout', { refreshToken });
        } catch {}

        // Remove session cleanup handler
        removeSessionCleanup();

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          rememberMe: true,
        });
      },

      checkAuth: async () => {
        const { accessToken, rememberMe } = get();

        if (!accessToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        // Re-setup session cleanup if needed (after page refresh)
        if (!rememberMe) {
          setupSessionCleanup();
        }

        try {
          const response = await api.get<User>('/auth/me');
          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          removeSessionCleanup();
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            rememberMe: true,
          });
        }
      },

      updateProfile: async (data) => {
        const response = await api.patch<User>('/auth/me', data);
        set({ user: response.data });
      },

      updatePassword: async (currentPassword, newPassword) => {
        await api.post('/auth/me/password', { currentPassword, newPassword });
      },

      uploadAvatar: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.postFormData<User>('/auth/me/avatar', formData);
        set({ user: response.data });
      },
    }),
    {
      name: 'yoda-client-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        rememberMe: state.rememberMe,
      }),
    }
  )
);
